export interface StoryNode {
  id: string
  text: string
  choices?: {
    text: string
    next: string
    feedback?: string
  }[]
  isEnding?: boolean
  endingType?: 'good' | 'neutral' | 'bad'
}
export interface Story {
  id: string
  title: string
  description: string
  ageRange: [number, number]
  category:
    | 'consent'
    | 'relationships'
    | 'safety'
    | 'health'
    | 'emotions'
    | 'online'
  difficulty: 'easy' | 'medium' | 'hard'
  estimatedTime: number
  nodes: StoryNode[]
}
// Helper function to get gender-appropriate crush name
function getCrushName(gender: string): string {
  if (gender === 'male') {
    return 'Priya' // Female name for male users
  } else if (gender === 'female') {
    return 'Rohan' // Male name for female users
  } else {
    return 'Alex' // Neutral name for non-binary/prefer-not-to-say
  }
}
function getFriendName(gender: string): string {
  return 'Priya'
}
// Generate gender-specific consent party story
function generateConsentPartyStory(gender: string): Story {
  const crushName = getCrushName(gender)
  const friendName = getFriendName(gender)
  const crushPronoun =
    gender === 'male' ? 'she' : gender === 'female' ? 'he' : 'they'
  const crushPossessive =
    gender === 'male' ? 'her' : gender === 'female' ? 'his' : 'their'
  const crushSubject =
    gender === 'male' ? 'She' : gender === 'female' ? 'He' : 'They'
  return {
    id: 'consent-party',
    title: 'The Party Decision',
    description: 'Navigate consent and boundaries at a party',
    ageRange: [15, 24],
    category: 'consent',
    difficulty: 'medium',
    estimatedTime: 5,
    nodes: [
      {
        id: 'start',
        text: `You're at your friend ${friendName}'s party. Everyone's having fun, dancing and chatting. Your crush, ${crushName}, comes up to you and asks if you want to go somewhere quieter to talk. What do you do?`,
        choices: [
          {
            text: `Say yes and go with ${crushPronoun}`,
            next: 'go-with-crush',
          },
          {
            text: 'Suggest staying where your friends can see you',
            next: 'stay-visible',
          },
          {
            text: 'Politely decline and stay with your friends',
            next: 'decline-stay',
          },
        ],
      },
      {
        id: 'go-with-crush',
        text: `You go to a quieter room with ${crushName}. ${crushSubject} starts getting closer and tries to kiss you. You're not sure if you're comfortable. What do you do?`,
        choices: [
          {
            text: 'Go along with it even though you feel unsure',
            next: 'go-along',
          },
          { text: 'Say you want to take things slow', next: 'take-slow' },
          { text: 'Say you need to go back to your friends', next: 'go-back' },
        ],
      },
      {
        id: 'stay-visible',
        text: `You suggest staying in the main area where friends are. ${crushName} smiles and says "That's cool, I just wanted to talk without shouting over the music!" You both find a quieter corner but stay where others can see you. ${crushSubject} respects your boundaries!`,
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'decline-stay',
        text: `You politely decline and explain you're having fun with your friends. ${crushName} says "No worries! Maybe we can hang out another time?" You appreciate that ${crushPronoun} respected your decision without pressure. Real interest respects boundaries!`,
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'go-along',
        text: `You go along with it even though you're uncomfortable. Afterward, you feel uneasy and wish you'd spoken up. Remember: Consent means enthusiastic YES, not just going along. Your comfort matters!`,
        isEnding: true,
        endingType: 'bad',
      },
      {
        id: 'take-slow',
        text: `You say "I like you, but I want to take things slow." ${crushName} smiles and says "I respect that! Let's just talk and get to know each other better." You feel relieved and happy. Setting boundaries is attractive and shows self-respect!`,
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'go-back',
        text: `You say you need to get back to your friends. ${crushName} looks disappointed but says "Okay, no problem." Later, ${crushPronoun} texts you: "Hey, I hope I didn't make you uncomfortable. Want to hang out sometime in a group setting?" You appreciate ${crushPossessive} respect for your boundaries.`,
        isEnding: true,
        endingType: 'good',
      },
    ],
  }
}
// Static stories that don't need gender customization
export const stories: Story[] = [
  {
    id: 'first-period',
    title: 'Understanding Your Period',
    description: 'Learn about menstruation and how to handle it confidently',
    ageRange: [13, 17],
    category: 'health',
    difficulty: 'easy',
    estimatedTime: 4,
    nodes: [
      {
        id: 'start',
        text: "You're at school and suddenly realize you got your period unexpectedly. You feel a bit panicked. What's your first step?",
        choices: [
          { text: 'Rush to the bathroom to check', next: 'bathroom-check' },
          { text: 'Tell your best friend immediately', next: 'tell-friend' },
          { text: 'Ask to see the school nurse', next: 'see-nurse' },
        ],
      },
      {
        id: 'bathroom-check',
        text: "You check and confirm you got your period. You don't have any supplies with you. What do you do?",
        choices: [
          {
            text: 'Use toilet paper temporarily and ask someone for help',
            next: 'good-ending-1',
          },
          {
            text: 'Try to hide it and hope for the best',
            next: 'bad-ending-1',
          },
          {
            text: 'Text your mom or guardian for help',
            next: 'good-ending-2',
          },
        ],
      },
      {
        id: 'tell-friend',
        text: 'Your friend is super supportive and says she has extra pads in her bag. She offers to come with you to the bathroom. This is what good friends do!',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'see-nurse',
        text: "The school nurse is understanding and gives you supplies. She also explains that it's completely normal and nothing to be embarrassed about. She offers to let you rest if you have cramps.",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'good-ending-1',
        text: "You use toilet paper as a temporary solution and ask a trusted friend or teacher for help. They provide you with a pad. You learn that it's always good to carry emergency supplies, but asking for help is perfectly okay!",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'good-ending-2',
        text: 'Your mom brings supplies to school and reassures you that this is a normal part of growing up. She reminds you to keep a small emergency kit in your bag. You feel supported and less anxious!',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'bad-ending-1',
        text: "You try to hide it but end up with visible staining on your clothes. This could have been avoided by asking for help. Remember: periods are natural, and people are usually understanding and willing to help. Next time, don't hesitate to reach out.",
        isEnding: true,
        endingType: 'bad',
      },
    ],
  },
  {
    id: 'body-confidence',
    title: 'Mirror, Mirror',
    description: 'Build body confidence and self-acceptance',
    ageRange: [13, 20],
    category: 'health',
    difficulty: 'easy',
    estimatedTime: 5,
    nodes: [
      {
        id: 'start',
        text: "You're getting ready for school and looking in the mirror. You start picking apart everything you don't like about your appearance. What do you do?",
        choices: [
          {
            text: 'Continue the negative self-talk',
            next: 'negative-spiral',
          },
          {
            text: 'Challenge the negative thoughts',
            next: 'challenge-thoughts',
          },
          { text: 'Focus on what your body can DO', next: 'focus-function' },
        ],
      },
      {
        id: 'negative-spiral',
        text: "You continue criticizing yourself. You feel worse and worse. This affects your mood for the whole day. You realize this pattern isn't healthy.",
        choices: [
          {
            text: 'Decide to change this habit',
            next: 'decide-change',
          },
        ],
      },
      {
        id: 'challenge-thoughts',
        text: 'You stop and ask yourself: "Would I talk to my best friend this way?" The answer is no. You start replacing negative thoughts with kinder ones.',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'focus-function',
        text: 'You shift focus: "My legs let me dance. My arms let me hug friends. My body is strong and capable." You feel empowered by what your body can do!',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'decide-change',
        text: 'You decide to practice self-compassion. You start a daily practice of saying one kind thing to yourself. Over time, your relationship with your body improves!',
        isEnding: true,
        endingType: 'good',
      },
    ],
  },
  {
    id: 'friendship-conflict',
    title: 'Best Friend Drama',
    description: 'Navigate a conflict with your best friend',
    ageRange: [12, 18],
    category: 'relationships',
    difficulty: 'easy',
    estimatedTime: 5,
    nodes: [
      {
        id: 'start',
        text: 'Your best friend has been ignoring you all week. You see them laughing with other friends. You feel hurt and confused. What do you do?',
        choices: [
          { text: 'Confront them directly', next: 'confront' },
          { text: 'Ignore them back', next: 'ignore-back' },
          { text: "Ask what's wrong", next: 'ask-whats-wrong' },
        ],
      },
      {
        id: 'confront',
        text: "You say 'Why are you ignoring me?' in an angry tone. They get defensive. The conversation escalates into an argument.",
        choices: [
          { text: 'Calm down and try again', next: 'calm-approach' },
          { text: 'Walk away angry', next: 'bad-ending' },
        ],
      },
      {
        id: 'ignore-back',
        text: "You ignore them too. Now you're both hurt and confused. The friendship is damaged by lack of communication.",
        isEnding: true,
        endingType: 'bad',
      },
      {
        id: 'ask-whats-wrong',
        text: "You calmly say 'Hey, I noticed you've been distant. Is everything okay? Did I do something wrong?' They open up about feeling left out when you hung out with other friends without inviting them.",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'calm-approach',
        text: 'You both calm down and talk it through. You learn that communication is key to resolving conflicts. Your friendship becomes stronger!',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'bad-ending',
        text: 'You walk away angry. The friendship ends badly because neither of you communicated. You learn that walking away from problems rarely solves them.',
        isEnding: true,
        endingType: 'bad',
      },
    ],
  },
  {
    id: 'social-anxiety',
    title: 'The Big Presentation',
    description: 'Manage anxiety about public speaking',
    ageRange: [13, 20],
    category: 'emotions',
    difficulty: 'medium',
    estimatedTime: 6,
    nodes: [
      {
        id: 'start',
        text: 'You have to give a presentation in class tomorrow. Your heart is racing just thinking about it. You feel anxious and scared. What do you do?',
        choices: [
          { text: 'Practice in front of a mirror', next: 'practice-mirror' },
          { text: 'Avoid thinking about it', next: 'avoid' },
          { text: 'Talk to someone about your anxiety', next: 'talk-anxiety' },
        ],
      },
      {
        id: 'practice-mirror',
        text: 'You practice several times. Each time, you feel a bit more confident. You also prepare some notes to help you remember key points.',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'avoid',
        text: "You avoid thinking about it until the last minute. The next day, you're even more anxious because you didn't prepare. You realize avoidance makes anxiety worse.",
        isEnding: true,
        endingType: 'bad',
      },
      {
        id: 'talk-anxiety',
        text: 'You talk to a teacher or counselor about your anxiety. They teach you breathing techniques and remind you that nervousness is normal. You feel supported!',
        isEnding: true,
        endingType: 'good',
      },
    ],
  },
  {
    id: 'peer-pressure-smoking',
    title: 'Saying No to Peer Pressure',
    description: 'Stand up to friends who want you to try smoking',
    ageRange: [14, 18],
    category: 'safety',
    difficulty: 'medium',
    estimatedTime: 5,
    nodes: [
      {
        id: 'start',
        text: "You're hanging out with friends. One pulls out cigarettes and offers you one. Everyone's looking at you. What do you do?",
        choices: [
          {
            text: "Say 'No thanks, I'm good' confidently",
            next: 'confident-no',
          },
          { text: 'Take it to fit in', next: 'bad-ending' },
          {
            text: 'Suggest doing something else',
            next: 'suggest-alternative',
          },
        ],
      },
      {
        id: 'confident-no',
        text: "You confidently say no. One friend says 'Come on!' but you stand firm. Another friend backs you up and also says no.",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'suggest-alternative',
        text: "You say 'Let's go get food instead!' A couple friends agree. You realize who your real friends are.",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'bad-ending',
        text: 'You take it to fit in. It tastes awful. Later, you feel guilty and realize you compromised your values. Real friends accept you for who you are.',
        isEnding: true,
        endingType: 'bad',
      },
    ],
  },
  {
    id: 'healthy-relationship',
    title: 'Green Flags vs Red Flags',
    description: 'Learn to identify healthy relationship signs',
    ageRange: [15, 22],
    category: 'relationships',
    difficulty: 'medium',
    estimatedTime: 6,
    nodes: [
      {
        id: 'start',
        text: "You're dating someone new. They want to know your phone password and get upset when you hang out with friends. What do you do?",
        choices: [
          {
            text: 'Give them your password to prove trust',
            next: 'give-password',
          },
          { text: 'Recognize this as a red flag', next: 'recognize-flag' },
          { text: 'Ask why they want it', next: 'ask-why' },
        ],
      },
      {
        id: 'give-password',
        text: "You give them your password. They start checking your messages constantly. You feel controlled and uncomfortable. This isn't healthy.",
        choices: [{ text: 'Set boundaries', next: 'set-boundaries' }],
      },
      {
        id: 'recognize-flag',
        text: "You recognize these as red flags - controlling behavior and isolation. You have a conversation about boundaries. If they don't respect them, you reconsider the relationship.",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'ask-why',
        text: "You ask why they want your password. They say 'If you have nothing to hide...' You realize this is manipulation. Healthy relationships are built on trust, not surveillance.",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'set-boundaries',
        text: 'You tell them you need privacy and the right to have friends. If they respect this, great. If not, you end the relationship. You learn that boundaries are essential!',
        isEnding: true,
        endingType: 'good',
      },
    ],
  },
  {
    id: 'mental-health-check',
    title: 'Feeling Down',
    description: 'Recognize when you need mental health support',
    ageRange: [14, 24],
    category: 'emotions',
    difficulty: 'medium',
    estimatedTime: 7,
    nodes: [
      {
        id: 'start',
        text: "You've been feeling sad for weeks. Things you used to enjoy don't interest you anymore. You're tired all the time. What do you do?",
        choices: [
          { text: 'Try to push through it alone', next: 'push-through' },
          { text: 'Talk to someone you trust', next: 'talk-someone' },
          { text: 'Research mental health resources', next: 'research' },
        ],
      },
      {
        id: 'push-through',
        text: 'You try to handle it alone, but it gets worse. You realize mental health struggles need support, just like physical health issues do.',
        choices: [{ text: 'Finally reach out for help', next: 'reach-out' }],
      },
      {
        id: 'talk-someone',
        text: 'You talk to a trusted adult. They listen and help you find a counselor. You learn that asking for help is strength, not weakness.',
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'research',
        text: "You research and find helplines and counseling resources. You reach out and start getting support. You're proud of taking this step!",
        isEnding: true,
        endingType: 'good',
      },
      {
        id: 'reach-out',
        text: "You finally reach out. A counselor helps you understand what you're experiencing. With support, you start feeling better. You wish you'd reached out sooner!",
        isEnding: true,
        endingType: 'good',
      },
    ],
  },
]
export function getStoriesForAge(age: number, gender?: string): Story[] {
  const genderToUse = gender || 'prefer-not-to-say'
  // Generate gender-specific consent party story
  const consentPartyStory = generateConsentPartyStory(genderToUse)
  // Filter other stories by age
  const otherStories = stories.filter(
    (story) => age >= story.ageRange[0] && age <= story.ageRange[1],
  )
  // Add consent party story if age appropriate
  if (age >= 15 && age <= 24) {
    return [consentPartyStory, ...otherStories]
  }
  return otherStories
}
export function getStoryById(id: string, gender?: string): Story | undefined {
  const genderToUse = gender || 'prefer-not-to-say'
  if (id === 'consent-party') {
    return generateConsentPartyStory(genderToUse)
  }
  return stories.find((story) => story.id === id)
}
